import {
  Component,
  computed,
  EventEmitter,
  input,
  OnInit,
  Output,
  resource,
  signal,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { SelectModule } from 'primeng/select';
import { FormFieldErrorComponent } from '../form-field-error/form-field-error.component';
import { BaseInputComponent } from './base/base-input.component';
import { FormsModule } from '@angular/forms';
import { TranslateKeyPipe } from 'src/app/pipes/translate-key/translate-key.pipe';
import { LanguageKey } from 'src/main/translate/language-key/language-key';
import { makeLoadEntityOptionsUseCase } from 'src/usecases/common/entity-options/load-entity-options/load-entity-options-use-case-factory';

@Component({
  selector: 'app-cmb-entity',
  standalone: true,
  imports: [
    CommonModule,
    SelectModule,
    FormsModule,
    FormFieldErrorComponent,
    TranslateKeyPipe,
  ],
  templateUrl: './cmb-entity.component.html',
})
export class CmbEntityComponent
  extends BaseInputComponent<any>
  implements OnInit
{
  private loadEntityOptionsUseCase = makeLoadEntityOptionsUseCase();
  @Output() optionsLoaded = new EventEmitter<any[]>();

  displayField = input<string>('name');
  valueField = input<string>('id');
  entity = input.required<string>();
  showNoneOption = input<boolean>(true);
  noneLabel = input<LanguageKey>(LanguageKey.NONE);
  filterable = input<boolean>(false);
  filterPlaceholder = input<LanguageKey>(LanguageKey.SEARCH);
  omittedValues = input<unknown[]>([]);
  omittedChildrenField = input<string | null>(null);
  omittedParentField = input<string | null>(null);

  loading = signal<boolean>(false);

  filteredOptions = computed(() => {
    const options = this.options.value() ?? [];
    const currentValue = this.value();
    const omittedValues = new Set(
      this.omittedValues().filter(
        (value) => value !== null && value !== undefined,
      ),
    );
    if (omittedValues.size === 0) return options;

    return options.filter((option) => {
      const optionValue = option?.[this.valueField()];
      if (optionValue === currentValue) return true;

      return (
        !omittedValues.has(optionValue) &&
        !this.hasOmittedChild(option, omittedValues) &&
        !this.hasOmittedAncestor(option, options, omittedValues)
      );
    });
  });

  private noneOption = computed(() => {
    const hasNoneOption = this.showNoneOption();
    if (!hasNoneOption) return null;

    const translatedNone = this.translateService.translateKey(this.noneLabel());
    const noneOption = {
      [this.displayField()]: translatedNone,
      [this.valueField()]: '',
    };

    return noneOption;
  });

  filterPlaceholderValue = computed(() =>
    this.translateService.translateKey(this.filterPlaceholder()),
  );

  options = resource({
    request: () => ({
      entity: this.entity(),
      displayField: this.displayField(),
      valueField: this.valueField(),
      showNoneOption: this.showNoneOption(),
      noneLabel: this.noneLabel(),
    }),
    loader: ({ request }) => this.fetchData(request.entity),
  });

  constructor() {
    super();
  }

  private async fetchData(entity: string) {
    try {
      this.loading.set(true);
      let items = await this.loadEntityOptionsUseCase.handle(entity);

      const none = this.noneOption();
      if (none) {
        items = [none, ...items];
      }

      this.optionsLoaded.emit(items);
      return items;
    } catch {
      this.optionsLoaded.emit([]);
      return [];
    } finally {
      this.loading.set(false);
    }
  }

  private hasOmittedChild(option: any, omittedValues: Set<unknown>): boolean {
    const omittedChildrenField = this.omittedChildrenField();
    if (!omittedChildrenField) return false;

    const children = option?.[omittedChildrenField];
    if (!Array.isArray(children)) return false;

    return children.some((child) =>
      omittedValues.has(child?.[this.valueField()]),
    );
  }

  private hasOmittedAncestor(
    option: any,
    options: any[],
    omittedValues: Set<unknown>,
  ): boolean {
    const omittedParentField = this.omittedParentField();
    if (!omittedParentField) return false;

    const optionsByValue = new Map(
      options.map((item) => [item?.[this.valueField()], item]),
    );

    const visitedValues = new Set<unknown>();
    let parentValue = option?.[omittedParentField];

    while (parentValue) {
      if (omittedValues.has(parentValue)) return true;
      if (visitedValues.has(parentValue)) return false;

      visitedValues.add(parentValue);
      parentValue = optionsByValue.get(parentValue)?.[omittedParentField];
    }

    return false;
  }

  onSelectChange(event: any) {
    this.onInput(event.value);
  }
}
